// src/pages/Dashboard.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchSessions, createStudySession, fetchGeneralReview, fetchUserPerformance } from '../api/api';
import AllSessionsModal from '../components/AllSessionsModal';
import SkillBarChart from '../components/SkillBarChart';
import ReactMarkdown from 'react-markdown'; // Import ReactMarkdown
import remarkGfm from 'remark-gfm';
import { useContext } from 'react';

import { SubscriptionContext } from '../SubscriptionContext';


import './Dashboard.css';

const skillNameMap = {
  "Words in Context": "words_in_context",
  "Text Structure and Purpose": "text_structure_and_purpose",
  "Cross-Text Connections": "cross_text_connections",
  "Central Ideas and Details": "central_ideas_and_details",
  "Command of Evidence": "command_of_evidence",
  "Inferences": "inferences",
  "Boundaries": "boundaries",
  "Form, Structure, and Sense": "form_structure_and_sense",
  "Rhetorical Synthesis": "rhetorical_synthesis",
  "Transitions": "transitions",

  "Linear equations in one variable": "linear_equations_in_one_variable",
  "Linear equations in two variables": "linear_equations_in_two_variables",
  "Linear functions": "linear_functions",
  "Systems of two linear equations in two variables": "systems_of_two_linear_equations_in_two_variables",
  "Linear inequalities in one or two variables": "linear_inequalities_in_one_or_two_variables",

  // Advanced Math
  "Equivalent expressions": "equivalent_expressions",
  "Nonlinear equations in one variable and systems of equations in two variables": "nonlinear_equations_in_one_variable_and_systems_of_equations_in_two_variables",
  "Nonlinear functions": "nonlinear_functions",

  // Problem-Solving and Data Analysis
  "Ratios, rates, proportional relationships, and units": "ratios_rates_proportional_relationships_and_units",
  "Percentages": "percentages",
  "One-variable data: distributions and measures of center and spread": "one_variable_data_distributions_and_measures_of_center_and_spread",
  "Two-variable data: models and scatterplots": "two_variable_data_models_and_scatterplots",
  "Probability and conditional probability": "probability_and_conditional_probability",
  "Inference from sample statistics and margin of error": "inference_from_sample_statistics_and_margin_of_error",
  "Evaluating statistical claims: observational studies and experiments": "evaluating_statistical_claims_observational_studies_and_experiments",

  // Geometry and Trigonometry
  "Area and volume": "area_and_volume",
  "Lines, angles, and triangles": "lines_angles_and_triangles",
  "Right triangles and trigonometry": "right_triangles_and_trigonometry",
  "Circles": "circles",
};

// Create a reverse map for displaying skill names
const skillDisplayNameMap = Object.fromEntries(
  Object.entries(skillNameMap).map(([key, value]) => [value, key])
);

const Dashboard = () => {
  const questionDomains = {
    "Reading and Writing": {
      "Craft and Structure": ["Words in Context", "Text Structure and Purpose", "Cross-Text Connections"],
      "Information and Ideas": ["Central Ideas and Details", "Command of Evidence", "Inferences"],
      "Standard English Conventions": ["Boundaries", "Form, Structure, and Sense"],
      "Expression of Ideas": ["Rhetorical Synthesis", "Transitions"]
    },
"Math": {
    "Algebra": [
      "Linear equations in one variable",
      "Linear equations in two variables",
      "Linear functions",
      "Systems of two linear equations in two variables",
      "Linear inequalities in one or two variables"
    ],
    "Advanced Math": [
      "Equivalent expressions",
      "Nonlinear equations in one variable and systems of equations in two variables",
      "Nonlinear functions"
    ],
    "Problem-Solving and Data Analysis": [
      "Ratios, rates, proportional relationships, and units",
      "Percentages",
      "One-variable data: distributions and measures of center and spread",
      "Two-variable data: models and scatterplots",
      "Probability and conditional probability",
      "Inference from sample statistics and margin of error",
      "Evaluating statistical claims: observational studies and experiments"
    ],
    "Geometry and Trigonometry": [
      "Area and volume",
      "Lines, angles, and triangles",
      "Right triangles and trigonometry",
      "Circles"
    ]
  }
  };

  const sectionNameMap = {
    "Reading and Writing": "reading_and_writing",
    "Math": "math",
  };

  const [selectedSkills, setSelectedSkills] = useState({});
  const [difficulty, setDifficulty] = useState('medium');
  const navigate = useNavigate();
  const [pastSessions, setPastSessions] = useState(null);
  const [selectedSection, setSelectedSection] = useState(Object.keys(questionDomains)[0]);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [showAllSessionsModal, setShowAllSessionsModal] = useState(false);
  const [generalReview, setGeneralReview] = useState(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [performanceData, setPerformanceData] = useState(null);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [performanceError, setPerformanceError] = useState(null);
  const [skillSelected, setSkillSelected] = useState(false);
  const { userSubscription, createCheckoutSession } = useContext(SubscriptionContext);

  const INITIAL_DISPLAY_COUNT = 5;

  useEffect(() => {
    const fetchPastSessions = async () => {
      try {
        const sessions = await fetchSessions();
        setPastSessions(sessions);
      } catch (error) {
        console.error("Error fetching sessions:", error);
        setPastSessions([]);
      }
    };

    const getGeneralReview = async () => {
      try {
        const data = await fetchGeneralReview();
        setGeneralReview(data.updated_review_text);

      } catch (error) {
        console.error("Failed to load performance review");
      }
    };

    const getPerformanceData = async () => {
      // Only fetch performance data for users with standard subscription
      if (true) {
        setPerformanceLoading(true);
        setPerformanceError(null);
        try {
          const data = await fetchUserPerformance();
          setPerformanceData(data);
        } catch (error) {
          console.error("Failed to load performance data:", error);
          setPerformanceError(error.message || "Failed to load performance data");
        } finally {
          setPerformanceLoading(false);
        }
      }
    };

    fetchPastSessions();
    getGeneralReview();
    getPerformanceData();
  }, [userSubscription?.subscription_tier]);

  const sortedSessions = useMemo(() => {
    if (!pastSessions) return [];
    // FIXED: Removed the extra 'new' from new new Date()
    return [...pastSessions].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [pastSessions]);

  const sessionsToDisplay = useMemo(() => {
    if (!sortedSessions) return [];
    return sortedSessions.slice(0, INITIAL_DISPLAY_COUNT);
  }, [sortedSessions]);

  const toggleSkill = (section, domain, skill) => {
    setSelectedSkills(prev => {
      setSkillSelected(true);
      const currentSection = prev[section] || {};
      const currentDomain = currentSection[domain] || [];
      const updatedSkills = currentDomain.includes(skill)
        ? currentDomain.filter(s => s !== skill)
        : [...currentDomain, skill];

      return {
        ...prev,
        [section]: { ...currentSection, [domain]: updatedSkills }
      };
    });

  };

  const selectAllInDomain = (section, domain) => {
    setSelectedSkills(prev => {
      const currentSection = prev[section] || {};
      setSkillSelected(true);
      return {
        ...prev,
        [section]: { ...currentSection, [domain]: [...questionDomains[section][domain]] }
      };
    });

  };
  const selectAllInSection = (section) => {
    setSelectedSkills(prev => {
      const newSectionSkills = {};
      const domainsForSection = questionDomains[section];
      setSkillSelected(true);
      for (const domain in domainsForSection) {
        newSectionSkills[domain] = [...domainsForSection[domain]];
      }
      return {
        ...prev,
        [section]: newSectionSkills
      };
    });

  };

  // --- NEW FUNCTION: Clear All Skills in a Section ---
  const clearAllInSection = (section) => {
    setSelectedSkills(prev => ({
      ...prev,
      [section]: {} // Set the selected skills for this section to an empty object
    }));
    setSkillSelected(false);
  };

  const clearAllInDomain = (section, domain) => {
    setSelectedSkills(prev => {
      const currentSection = prev[section] || {};
      const updatedSection = { ...currentSection };
      delete updatedSection[domain]; // Remove the domain entirely
      // Or alternatively: updatedSection[domain] = []; // Set to empty array
      
      return {
        ...prev,
        [section]: updatedSection
      };
    });
  };
  
  const handleCreateSession = (mode) => {
    let flatSkills = [];
    for (const section in selectedSkills) {
      for (const domain in selectedSkills[section]) {
        flatSkills = flatSkills.concat(selectedSkills[section][domain]);
      }
    }
  
    const mappedSkills = flatSkills.map(skill => skillNameMap[skill]);
  
    // Convert selectedSection to its snake_case equivalent]

    console.log(selectedSection);
    const mappedSection = sectionNameMap[selectedSection];

    console.log(mappedSection);
  
    // If you also need to map the domains (e.g., "Craft and Structure" to "craft_and_structure")
    // you would need to adjust how selectedSkills are structured or transformed here
    // before sending them. For now, we're assuming 'mappedSkills' is enough.
  
    navigate('/study-session', {
      state: {
        selectedSection: mappedSection, // Use the mapped section name
        selectedSkills: mappedSkills,
        difficulty: difficulty,
        mode: mode,
      }
    });
  };
  const handleReviewSession = (sessionId) => {
    navigate(`/review-session/${sessionId}`);

  };

  const canUsePersonalizedSession = userSubscription?.subscription_status === "active" || userSubscription?.subscription_status === "trialing" || userSubscription?.subscription_status == "free";

  return (
    <div className="page-content dashboard-page">
      <h1>Dashboard</h1>
      <br></br>

     
      

        <SkillBarChart 
          skillScores={performanceData?.skill_scores || []}
          isLoading={performanceLoading}
          error={performanceError}
        />



      <div className='personalized-section'>
      <h4>Personalized Practice Session</h4>
      <p>Practice your weak areas using our cutting-edge algorithm that identifies what skill areas you need to improve on, and adjusts the questions you recieve accordingly!</p>
      {canUsePersonalizedSession ? (
  <button 
    className="create-personalized-session-btn"
    onClick={() => handleCreateSession("personalized")}
    disabled={isCreatingSession}
  >
    {isCreatingSession ? 'Creating Personalized Session...' : 'Create Personalized Study Session'}
  </button>
) : (
  <div className="personalized-session-container">
    <button
      className="create-personalized-session-btn disabled"
      disabled={true}
    >
      Create Personalized Study Session
    </button>
    <div className="upgrade-link-container">
      <button
        onClick={() => createCheckoutSession('standard')}
        className="upgrade-session-button"
      >
        Upgrade account to use personalized sessions
      </button>
    </div>
  </div>
)}

</div>

<div className="manual-session-section">
      <h3>Create Manual Study Session</h3>
      <div className="section-tabs">
        {Object.keys(questionDomains).map(section => (
          <button
            key={section}
            className={`tab-button ${selectedSection === section ? 'active' : ''}`}
            onClick={() => {
              setSelectedSection(section);
              clearAllInSection(section);
            }}
          >
            {section}
          </button>
        ))}
      </div>

      <div className="difficulty-select">
        <label htmlFor="difficulty">Select Difficulty: </label>
        <select
          id="difficulty"
          value={difficulty}
          onChange={(e) => setDifficulty(e.target.value)}
        >
          <option value="easy">Easy</option>
          <option value="medium">Medium</option>
          <option value="hard">Hard</option>
        </select>
      </div>
      {/* --- NEW: Section-wide actions (Select All / Clear All) --- */}
      <div className="section-wide-actions">
        <button
          onClick={() => selectAllInSection(selectedSection)}
          className="select-all-skills-btn"
        >
          Select All Skills in {selectedSection}
        </button>
        <button
          onClick={() => clearAllInSection(selectedSection)}
          className="clear-all-skills-btn"
        >
          Clear All Skills in {selectedSection}
        </button>
      </div>

      {Object.entries(questionDomains).map(([section, domains]) => (
  selectedSection === section && (
    <div key={section} className="skill-selection-section">
      <div className="domain-grid">
        {Object.entries(domains).map(([domain, skills]) => (
          <div key={domain} className="domain-section">
            <h3>{domain}</h3>
            <button
              onClick={() => selectAllInDomain(section, domain)}
              className="select-all-button"
            >
              Select All
            </button>
            <div className="skills-grid">
              {skills.map(skill => (
                <label key={skill} className="skill-checkbox-label">
                  <input
                    type="checkbox"
                    checked={selectedSkills[section]?.[domain]?.includes(skill) || false}
                    onChange={() => toggleSkill(section, domain, skill)}
                  />
                  <span className="skill-checkbox-text">{skill}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
))}

      <button
        className="create-session-btn"
        onClick={() => handleCreateSession("manual")}
        disabled={isCreatingSession || !skillSelected}
      >
        {isCreatingSession ? 'Creating Session...' : !skillSelected ? 'Please select skill categories!' : 'Create Study Session'}
        
      </button>
      </div>

      <h3>Past Sessions</h3>
      {pastSessions === null ? (
        <p className="loading-message">Loading past sessions...</p>
      ) : sortedSessions.length > 0 ? (
        <>
          <ul className="past-sessions-list">
            {/* THIS IS THE LINE TO CHANGE: */}
            {sessionsToDisplay.map((session) => ( 
              <li key={session.session_id} className="session-item">
                <div className="session-info">
                  
                  <strong>Date:</strong> {session.timestamp ? new Date(session.timestamp).toLocaleString() : 'N/A'}<br/>
                  <strong>Skills:</strong> {session.skills.map(s => skillDisplayNameMap[s] || s).join(', ')}<br/>
                  <strong>Difficulty:</strong> {session.difficulty}
                </div>
                <button
                  className="review-session-btn"
                  onClick={() => handleReviewSession(session.session_id)}
                >
                  Review Session
                </button>
              </li>
            ))}
          </ul>
          {sortedSessions.length > INITIAL_DISPLAY_COUNT && (
            <button
              className="view-all-sessions-btn"
              onClick={() => setShowAllSessionsModal(true)}
            >
              View All {sortedSessions.length} Sessions
            </button>
          )}
        </>
      ) : (
        <p className="no-sessions-message">No past sessions found.</p>
      )}

      {showAllSessionsModal && pastSessions && (
        <AllSessionsModal
          sessions={sortedSessions}
          onClose={() => setShowAllSessionsModal(false)}
        />
      )}
    </div>
  );
};


export default Dashboard;
